import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // Load from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("cart");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist cart
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // ADD TO CART (variant-based)
  const addToCart = useCallback((product, quantity = 1) => {
    if (!product) return;

    // pick default or first variant
    const variant =
      product.variants?.find((v) => v.isDefault) || product.variants?.[0];

    if (!variant) {
      console.error("No variant found for product:", product);
      return;
    }

    const variantId = variant.id;

    setCart((prev) => {
      const existing = prev.find((item) => item.variantId === variantId);

      if (existing) {
        return prev.map((item) =>
          item.variantId === variantId
            ? {
                ...item,
                quantity: Math.min(
                  item.quantity + quantity,
                  item.availableQuantity ?? Infinity,
                ),
              }
            : item,
        );
      }

      return [
        ...prev,
        {
          variantId: variant.id,
          productId: product.id,
          productName: product.productName,
          sku: variant.sku,
          price: variant.price,
          currency: variant.currency,
          image: variant.mainImageUrl,
          attributes: variant.attributes || {},
          quantity: Math.min(quantity, variant.availableQuantity ?? Infinity),
          availableQuantity: variant.availableQuantity,
        },
      ];
    });
  }, []);

  // REMOVE
  const removeFromCart = useCallback((variantId) => {
    setCart((prev) => prev.filter((item) => item.variantId !== variantId));
  }, []);

  // UPDATE QUANTITY
  const updateQuantity = useCallback((variantId, quantity) => {
    setCart((prev) =>
      prev.map((item) =>
        item.variantId === variantId
          ? {
              ...item,
              quantity: Math.min(
                Math.max(quantity, 1),
                item.availableQuantity ?? Infinity,
              ),
            }
          : item,
      ),
    );
  }, []);

  // CLEAR CART
  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // CHECK EXISTENCE
  const isInCart = useMemo(
    () => (variantId) => cart.some((item) => item.variantId === variantId),
    [cart],
  );

  // TOTALS
  const { totalPrice, cartCount } = useMemo(() => {
    const totalPrice = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );

    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    return { totalPrice, cartCount };
  }, [cart]);

  //  CONTEXT VALUE
  const value = useMemo(
    () => ({
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      totalPrice,
      cartCount,
      isInCart,
    }),
    [
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      totalPrice,
      cartCount,
      isInCart,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
